import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import omitBy from 'lodash/omitBy';

type SearchParams = Record<string, string>;
type SearchParamsNavigationOptions = {
  replace?: boolean;
};

const sortedQueryString = (search: string): string => {
  const params = new URLSearchParams(search);
  params.sort();
  return params.toString();
};

export const useSearchParamsWithValues = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = useMemo<SearchParams>(
    () =>
      location.search
        ? Object.fromEntries(new URLSearchParams(location.search))
        : {},
    [location.search],
  );

  const setSearchParams = useCallback(
    (
      newValues: SearchParams,
      { replace = true }: SearchParamsNavigationOptions = {},
    ) => {
      const searchPath = new URLSearchParams(
        omitBy(newValues, v => !v),
      ).toString();

      const hasSearch = Boolean(searchPath);
      const nextUrl = hasSearch
        ? `${location.pathname}?${searchPath}`
        : location.pathname;

      if (
        sortedQueryString(location.search) === sortedQueryString(searchPath)
      ) {
        return;
      }

      navigate(nextUrl, { replace });
    },
    [navigate, location.pathname, location.search],
  );

  const updateSearchParams = useCallback(
    (newValues: SearchParams, options: SearchParamsNavigationOptions = {}) =>
      setSearchParams({ ...searchParams, ...newValues }, options),
    [searchParams, setSearchParams],
  );

  return {
    searchParams,
    setSearchParams,
    updateSearchParams,
  };
};
